import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  activateOperation,
  activateSubcategory,
  createOperation,
  createSubcategory,
  removeOperation,
  removeSubcategory,
  saveOperation,
  saveSubcategory,
  subcategoriesShellQuery,
  subcategoryOperationsQuery,
  subcategoryQuery,
  subcategoryTableQuery,
  type Operation,
  type SubcategoryCategory,
} from '../../api/settingsEaseedeskSubcategories'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket subcategories and operations.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_subcategories/index.php` + `operations.php` + `operationList.php`,
 * coloana cu coloana fata de `subcategoriesModel` din
 * `public/js/settings/easeedesk/tickets_settings.js`, si gest cu gest fata de
 * `index-1.js`, `operations-1.js` si `operationList-1.js`.
 *
 * CE NU MAI E, SI DE CE
 * ---------------------
 *
 * Modalul de editare a unei operatiuni avea si un camp "SLA". El se trimitea la
 * server, iar `Ticket_operation::update()` scria numai `operation_name` si
 * `estimate_effort` - deci campul acela nu s-a salvat niciodata, si nici nu
 * exista coloana pentru el. Un camp care se poarta ca si cum ar salva ceva e
 * mai rau decat unul care lipseste, deci nu vine incoace.
 */

const TABLE_ID = 'ticket-subcategories'

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `Ticket_subcategory::subcategoryActionsForm()`, litera cu litera. */
export function subcategoryActionsHtml(id: number): string {
  return (
    '<div class="btn-group" role="group" aria-label="...">\n' +
    '                    <button type="button" data-subcategory="' +
    id +
    '" class="btn btn-sm btn-default edit-subcategory" data-toggle="modal" data-target="#editSubcategoryModal"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>\n' +
    '                    <button type="button" data-subcategory="' +
    id +
    '" class="btn btn-sm btn-default remove-subcategory"><i class="fas fa-times" aria-hidden="true"></i> Remove</button>\n' +
    '                </div>'
  )
}

/** `subcategoriesModel`: aceleasi sase coloane, in aceeasi ordine. */
export function subcategoryColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Subcategory',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.subcategory_name ?? '')),
    },
    {
      index: 2,
      title: 'Activate',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<input type="checkbox" ${Number(row.active) === 1 ? 'checked' : ''} class="activate-subcategory" value="${Number(row.ID)}"/>`,
    },
    {
      index: 3,
      title: 'Category',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.category_name ?? '')),
    },
    {
      index: 4,
      title: 'Operations',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<span class="label label-success">${Number(row.operations ?? 0)}</span>`,
    },
    {
      index: 5,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) => subcategoryActionsHtml(Number(row.ID)),
    },
  ]
}

/** Coloana SQL a fiecarui indice, ca in harta din `getSubcategoryList()`. */
export const ORDER_NAMES = ['ID', 'subcategory_name', 'active', 'category_name', 'operations', 'ID']

export function TicketSubcategoriesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(subcategoriesShellQuery())
  /* `order: [[1, 'desc']]` - pe ce si cum porneste lista veche. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const [edited, setEdited] = useState(0)
  const [creating, setCreating] = useState(false)
  const columns = useMemo(() => subcategoryColumns(), [])
  const table = useQuery({
    ...subcategoryTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'ID',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_subcategories'] })
  }

  const activate = useMutation({
    mutationFn: (v: { subcategory: number; active: boolean }) => activateSubcategory(v.subcategory, v.active),
    onSuccess: redraw,
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      redraw()
    },
  })

  const remove = useMutation({
    mutationFn: (subcategory: number) => removeSubcategory(subcategory),
    onSuccess: redraw,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  /* Celulele sunt HTML pus cu innerHTML, ca in portal: clicul se prinde sus, pe
     delegare, cum il prindea jQuery. */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const edit = target.closest('.edit-subcategory')
    if (edit instanceof HTMLElement) {
      event.preventDefault()
      setEdited(Number(edit.dataset.subcategory ?? 0))
      return
    }

    const kill = target.closest('.remove-subcategory')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      if (!window.confirm('Are you sure?\nYou will not be able to recover this subcategory!')) return
      remove.mutate(Number(kill.dataset.subcategory ?? 0))
      return
    }

    const box = target.closest('input.activate-subcategory')
    if (box instanceof HTMLInputElement) {
      activate.mutate({ subcategory: Number(box.value), active: box.checked })
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new subcategory
      </button>
      <h4>
        <strong>Ticket</strong> subcategories and operations
      </h4>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="A subcategory groups the operations a ticket can be about."
        details={(row) => <OperationsPanel subcategory={Number(row.ID)} onChanged={redraw} />}
      />
      <NewSubcategoryModal
        open={creating}
        categories={shell.data.categories}
        onClose={() => setCreating(false)}
        onSaved={redraw}
      />
      <EditSubcategoryModal subcategory={edited} onClose={() => setEdited(0)} onSaved={redraw} />
    </div>
  )
}

/**
 * Randul desfasurat: `subcategoryDetails()` randa `operations.php`, care isi
 * aducea singur lista cu `getSubcategoryOperations` (`operationList.php`).
 * Aici e o singura cerere, dar acelasi markup - `.operations-list` cu tabelul
 * de patru coloane, sau caseta "No operations found."
 */
export function OperationsPanel({ subcategory, onChanged }: { subcategory: number; onChanged(): void }) {
  const queryClient = useQueryClient()
  const query = useQuery(subcategoryOperationsQuery(subcategory))
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<Operation | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_subcategories'] })
    onChanged()
  }

  const flip = useMutation({
    mutationFn: (v: { operation: number; active: boolean }) => activateOperation(v.operation, v.active),
    onSuccess: () => {
      toast('Operation status changed.', 'Success')
      reload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      reload()
    },
  })

  const remove = useMutation({
    mutationFn: (operation: number) => removeOperation(operation),
    onSuccess: reload,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const operations = query.data?.operations ?? []

  return (
    <div style={{ padding: '0px 15px' }}>
      <div style={{ marginBottom: 20, marginTop: 10 }} className="row">
        <div className="col-md-6">
          <h4>Operations</h4>
        </div>
        <div className="col-md-6 aright">
          <button
            type="button"
            className="btn btn-default btn-sm btn-primary new-operation"
            data-subcategory={subcategory}
            onClick={() => setCreating(true)}
          >
            <i className="fas fa-plus" aria-hidden="true"></i> New operation
          </button>
        </div>
      </div>

      <div className="list-group operations-list">
        {query.error ? (
          <LoadFailed error={query.error} onRetry={() => void query.refetch()} what="The operations" />
        ) : !query.data ? (
          <Spinner />
        ) : (
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th width={100}></th>
                <th className="a-left">Operation name</th>
                <th className="a-left">Real effort</th>
                <th width={200}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {operations.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle" aria-hidden="true"></i> No operations found.
                    </div>
                  </td>
                </tr>
              ) : (
                operations.map((operation) => (
                  <tr key={operation.ID} id={`operation_${operation.ID}`}>
                    <td>
                      <input
                        type="checkbox"
                        className="icheck-operation"
                        value={operation.ID}
                        checked={operation.active === 1}
                        onChange={(e) => flip.mutate({ operation: operation.ID, active: e.target.checked })}
                      />
                    </td>
                    <td>
                      <label style={{ marginLeft: 10 }}>{operation.operation_name}</label>
                    </td>
                    <td>
                      <label style={{ marginLeft: 10 }}>{operation.estimate_effort} minutes</label>
                    </td>
                    <td>
                      <div className="btn-group" role="group" aria-label="...">
                        <button
                          type="button"
                          data-operation={operation.ID}
                          className="btn btn-sm btn-primary edit-operation"
                          onClick={() => setEdited(operation)}
                        >
                          <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                        </button>
                        <button
                          type="button"
                          data-operation={operation.ID}
                          className="btn btn-sm btn-danger remove-operation"
                          onClick={() => {
                            if (!window.confirm('Are you sure?\nYou will not be able to recover this operation!')) return
                            remove.mutate(operation.ID)
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true"></i> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <OperationModal
        subcategory={subcategory}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <OperationModal
        subcategory={subcategory}
        operation={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newSubcategoryModal` din macheta: numele si categoria in care intra. */
export function NewSubcategoryModal({
  open,
  categories,
  onClose,
  onSaved,
}: {
  open: boolean
  categories: SubcategoryCategory[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [category, setCategory] = useState(0)

  useEffect(() => {
    if (!open) return
    setName('')
    setCategory(0)
  }, [open])

  const create = useMutation({
    mutationFn: () => createSubcategory(category, name.trim()),
    onSuccess: () => {
      toast('New subcategory added.', 'Success')
      onClose()
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newSubcategoryModal" labelledBy="newSubcategoryModalLabel" open={open} onClose={onClose} title="New subcategory">
      <form
        id="new-subcategory-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '' || category < 1) return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="subcategory-name" className="control-label">
            Subcategory name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="subcategory-name"
            name="subcategory_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="category" className="control-label">
            Category
          </label>
          <select
            className="form-control input-sm"
            id="category"
            name="category"
            value={category === 0 ? '' : String(category)}
            onChange={(e) => setCategory(Number(e.target.value))}
          >
            <option value="">Select</option>
            {categories.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.category_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-subcategory"
            disabled={name.trim() === '' || category < 1 || create.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** `editSubcategoryModal`: numele se cere de la server, ca in `index-1.js`. */
export function EditSubcategoryModal({
  subcategory,
  onClose,
  onSaved,
}: {
  subcategory: number
  onClose(): void
  onSaved(): void
}) {
  const query = useQuery(subcategoryQuery(subcategory))
  const [name, setName] = useState('')

  useEffect(() => {
    if (query.data && query.data.ID === subcategory) setName(query.data.subcategory_name)
  }, [query.data, subcategory])

  const save = useMutation({
    mutationFn: () => saveSubcategory(subcategory, name.trim()),
    onSuccess: () => {
      toast('Settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="editSubcategoryModal"
      labelledBy="editSubcategoryModalLabel"
      open={subcategory > 0}
      onClose={onClose}
      title="Edit subcategory"
    >
      {!query.data ? (
        <Spinner />
      ) : (
        <form
          id="edit-subcategory-form"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() === '') return
            save.mutate()
          }}
        >
          <div className="form-group">
            <label htmlFor="edit-subcategory-name" className="control-label">
              Subcategory name
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="edit-subcategory-name"
              name="subcategory_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary save-subcategory" disabled={name.trim() === '' || save.isPending}>
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

/**
 * `newOperationModal` si `editOperationModal`: acelasi formular, cu numele si
 * efortul in minute. Marginea din macheta (`max="1439"`) e pastrata pe camp si
 * asezata si pe server.
 */
export function OperationModal({
  subcategory,
  operation,
  open,
  onClose,
  onSaved,
}: {
  subcategory: number
  operation?: Operation
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = operation !== undefined
  const [name, setName] = useState('')
  const [effort, setEffort] = useState('')

  useEffect(() => {
    if (!open) return
    setName(operation?.operation_name ?? '')
    setEffort(operation === undefined ? '' : String(operation.estimate_effort))
  }, [open, operation])

  const save = useMutation({
    mutationFn: () =>
      editing
        ? saveOperation(operation.ID, name.trim(), Number(effort || 0))
        : createOperation(subcategory, name.trim(), Number(effort || 0)),
    onSuccess: () => {
      toast(editing ? 'Settings saved.' : 'New operation added.', 'Success')
      onClose()
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editOperationModal' : 'newOperationModal'}
      labelledBy={editing ? 'editOperationModalLabel' : 'newOperationModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit operation' : 'New operation'}
    >
      <form
        id={editing ? 'edit-operation-form' : 'new-operation-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor={editing ? 'edit-operation-name' : 'operation-name'} className="control-label">
            Operation name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id={editing ? 'edit-operation-name' : 'operation-name'}
            name="operation_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor={editing ? 'edit-estimate-effort' : 'estimate_effort'} className="control-label">
            Real effort{' '}
            <i
              className="fas fa-info-circle color-blue font-14"
              style={{ cursor: 'default' }}
              data-toggle="tooltip"
              data-placement="top"
              title="Service Level Agreement (in minutes)"
            >
              {' '}
            </i>
          </label>
          <input
            type="number"
            className="form-control input-sm"
            id={editing ? 'edit-estimate-effort' : 'estimate_effort'}
            name="estimate_effort"
            max={1439}
            min={0}
            value={effort}
            onChange={(e) => setEffort(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={editing ? 'btn btn-primary save-operation' : 'btn btn-primary submit-new-operation'}
            disabled={name.trim() === '' || save.isPending}
          >
            {editing ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
